import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Avatar } from "@mui/material";

import TruncatedWithTooltip from "../../../components/TruncatedWithTooltip";
import { UITable, getUserInitials } from "../../../components/ui";
import EmployeesRowActionsMenu from "./EmployeesRowActionsMenu";

const EMPTY_CELL = "-";

const displayCellValue = (value) => {
  if (value == null || String(value).trim() === "") return EMPTY_CELL;
  return value;
};

const renderTruncatedCell = ({ value }) => {
  const text = displayCellValue(value);
  return (
    <TruncatedWithTooltip title={text}>
      <span className="MuiDataGrid-cellContent">{text}</span>
    </TruncatedWithTooltip>
  );
};

const EmployeesTable = ({
  rows,
  labels,
  pageSize,
  onView,
  onEdit,
  onRemove,
  canEdit = true,
  canDelete = true,
}) => {
  const showActions = canEdit || canDelete;

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: labels.name,
        sortable: false,
        flex: 1,
        minWidth: 240,
        maxWidth: 320,
        renderCell: ({ row }) => (
          <div className="team-management__name-cell">
            <Avatar
              src={row.image || undefined}
              alt={row.name}
              className="team-management__avatar"
            >
              {getUserInitials(row.name)}
            </Avatar>
            <div className="team-management__name-text">
              <TruncatedWithTooltip title={row.name}>
                <span className="MuiDataGrid-cellContent">{row.name}</span>
              </TruncatedWithTooltip>
            </div>
          </div>
        ),
      },
      {
        field: "email",
        headerName: labels.email,
        sortable: false,
        flex: 1.6,
        minWidth: 300,
        renderCell: renderTruncatedCell,
      },
      {
        field: "mobileNumber",
        headerName: labels.mobileNumber,
        sortable: false,
        flex: 1,
        minWidth: 160,
        renderCell: renderTruncatedCell,
      },
      {
        field: "role",
        headerName: labels.role,
        sortable: false,
        flex: 1.1,
        minWidth: 200,
        renderCell: renderTruncatedCell,
      },
      ...(showActions
        ? [
            {
              field: "actions",
              headerName: labels.actions,
              sortable: false,
              flex: 0.4,
              minWidth: 80,
              align: "right",
              headerAlign: "right",
              renderCell: ({ row }) => (
                <EmployeesRowActionsMenu
                  row={row}
                  labels={labels}
                  onEdit={onEdit}
                  onDelete={onRemove}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ),
            },
          ]
        : []),
    ],
    [labels, onEdit, onRemove, onView, showActions, canEdit, canDelete],
  );

  return (
    <div className="team-management__table team-management__table--clickable">
      <UITable
        rows={rows}
        columns={columns}
        pageSize={pageSize}
        contentSized
        hideFooter
        disableColumnMenu
        disableSelectionOnClick
        getRowId={(row) => row.id}
        rowHeight={70}
        onRowClick={({ row }) => onView(row)}
      />
    </div>
  );
};

EmployeesTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  pageSize: PropTypes.number,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  labels: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    mobileNumber: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    actions: PropTypes.string.isRequired,
    edit: PropTypes.string.isRequired,
    remove: PropTypes.string.isRequired,
  }).isRequired,
};

export default EmployeesTable;
